import { applyOperator } from '../../model/condition/operators.js'
/**
 * Generic condition matcher — the shared predicate primitive behind every
 * validation guard. Runs on both client and server (it lives in `common/`).
 *
 * A condition is `{ field, operator, value | valueFrom }`:
 *
 *   { field: 'inventory',  operator: 'notEmpty' }
 *   { field: 'state',      operator: 'in',     value: ['draft', 'open'] }
 *   { field: 'locationId', operator: 'equals', valueFrom: 'id' }
 *
 * `value` is a literal. `valueFrom` reads a path off the *source* record — the
 * one being deleted/validated — so guards never hardcode ids.
 *
 * Conditions compose. `{ any: [...] }` is an OR group, `{ all: [...] }` an AND
 * group, and they nest:
 *
 *   { any: [ { field: 'fromLocationId', operator: 'equals', valueFrom: 'id' },
 *            { field: 'toLocationId',   operator: 'equals', valueFrom: 'id' } ] }
 *
 * `field` supports dotted paths and array hops, so a condition can reach into
 * line items without the caller flattening anything first:
 *
 *   'address.city'            → row.address.city
 *   'lines[].fromLocationId'  → [ every line's fromLocationId ]
 */

// Walk a path against a value. `[]` on a segment flattens that level, and any
// remaining segments are mapped over its items — so `lines[].id` yields an
// array of ids, not undefined.
const walk = (value, segments) => {
    if (!segments.length) return value
    if (value === undefined || value === null) return undefined

    const [raw, ...rest] = segments
    const isArrayHop = raw.endsWith('[]')
    const key = isArrayHop ? raw.slice(0, -2) : raw
    const next = key ? value[key] : value

    if (!isArrayHop) return walk(next, rest)
    if (!Array.isArray(next)) return []
    if (!rest.length) return next

    return next
        .flatMap(item => {
            const resolved = walk(item, rest)
            return Array.isArray(resolved) ? resolved : [resolved]
        })
        .filter(v => v !== undefined)
}

export const resolvePath = (obj, path) => {
    if (!path) return undefined
    return walk(obj, String(path).split('.'))
}

// "Empty" means absent or a container with nothing in it. `0` and `false` are
// values, not emptiness — use gt/equals for those.
export { isEmpty } from '../../model/condition/operators.js'

// Loose equality so a JSONB '5' and a numeric 5 compare equal — Supabase returns
// `data->>field` as text, and guards are written against typed model values.
// Loose equality: guards read rows out of JSONB, where Supabase returns
// `data->>field` as text and a typed 5 must still match '5'.
const compare = (operator, actual, expected) =>
    applyOperator(operator, actual, expected, { loose: true })

/**
 * Evaluate one condition (or an any/all group) against a record.
 * @param {Object} record  - the row being tested
 * @param {Object} condition - { field, operator, value|valueFrom } or { any: [] } / { all: [] }
 * @param {Object} source  - the record being validated, for `valueFrom` lookups
 * @returns {boolean}
 */
const matchCondition = (record, condition, source = {}) => {
    if (!condition) return false

    if (Array.isArray(condition.any)) return condition.any.some(c => matchCondition(record, c, source))
    if (Array.isArray(condition.all)) return condition.all.every(c => matchCondition(record, c, source))

    const { field, operator = 'exists' } = condition
    const actual = resolvePath(record, field)
    const expected = condition.valueFrom !== undefined
        ? resolvePath(source, condition.valueFrom)
        : condition.value

    return compare(operator, actual, expected)
}

export default matchCondition
